import React, { useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Modal,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { PromotionTimePicker } from "./PromotionTimePicker";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useFormik } from "formik";
import * as yup from "yup";
import { RequiredAsterisk } from "../../UI/RequiredAsterisk/RequiredAsterisk";
import { useUpdatePromotionInviteMutation } from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { useAlert } from "../../../hooks/useAlert";
import {
  DESCRIPTION_MAX_LENGTH,
  descriptionQuillModules,
  getDescriptionLength,
} from "./quillDescriptionLimit";
import {
  combineEventDatePayload,
  hasEventTimeValue,
  promotionEventDateSchema,
  promotionEventTimeSchema,
  promotionInstantToPicker,
  promotionTodayPickerValue,
  syncEventDateField,
} from "./promotionEventDateTime";
import { FieldCharCount } from "./FieldCharCount";
import {
  cappedTextSchema,
  clampFieldLength,
  inviteSendBlockedMessage,
  locationUrlSchema,
  normalizeLocationUrl,
  PROMOTION_NAME_MAX_LENGTH,
  PROMOTION_VENUE_MAX_LENGTH,
  setClampedField,
  stripHtml,
} from "./promotionInviteFields";
import "./CreatePromotionInviteModal.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  p: "28px 34px 34px",
  outline: "none",
};

const quillFormats = [
  "bold",
  "italic",
  "underline",
  "align",
  "list",
  "bullet",
];

const validationSchema = yup.object({
  eventName: cappedTextSchema(
    PROMOTION_NAME_MAX_LENGTH,
    `Event name cannot exceed ${PROMOTION_NAME_MAX_LENGTH} characters`,
    { required: "Event name is required" }
  ),
  description: yup
    .string()
    .test(
      "description-required",
      "Description is required",
      (value) => Boolean(stripHtml(value))
    )
    .test(
      "description-max",
      `Description cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`,
      (value) => getDescriptionLength(value) <= DESCRIPTION_MAX_LENGTH
    ),
  eventDate: promotionEventDateSchema(),
  eventTime: promotionEventTimeSchema({ required: false }),
  venue: cappedTextSchema(
    PROMOTION_VENUE_MAX_LENGTH,
    `Venue cannot exceed ${PROMOTION_VENUE_MAX_LENGTH} characters`
  ),
  locationUrl: locationUrlSchema(),
});

const buildInitialValues = (invite) => ({
  eventName: clampFieldLength(invite?.name || "", PROMOTION_NAME_MAX_LENGTH),
  description: invite?.description || "",
  eventDate: invite?.eventDate ? promotionInstantToPicker(invite.eventDate) : null,
  eventTime:
    invite?.hasEventTime !== false && invite?.eventDate
      ? promotionInstantToPicker(invite.eventDate)
      : null,
  venue: clampFieldLength(invite?.venue || "", PROMOTION_VENUE_MAX_LENGTH),
  locationUrl: invite?.locationUrl || "",
});

const SEND_FIELD_VENUE = "Venue Address";
const SEND_FIELD_TIME = "Time";

export const EditPromotionInviteModal = ({
  open,
  onClose,
  invite,
  onSuccess,
  missingSendFields = [],
}) => {
  const { displayAlert } = useAlert();
  const [updateInvite, { isLoading }] = useUpdatePromotionInviteMutation();

  const formik = useFormik({
    initialValues: buildInitialValues(invite),
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const { eventDate: eventDateIso, hasEventTime } = combineEventDatePayload(
        values.eventDate,
        values.eventTime
      );
      if (!eventDateIso) {
        displayAlert({
          open: true,
          message: "Valid date is required",
          type: "error",
          timeout: "2500",
        });
        return;
      }

      try {
        await updateInvite({
          inviteId: invite._id,
          name: values.eventName.trim(),
          description: values.description,
          eventDate: eventDateIso,
          hasEventTime,
          venue: values.venue.trim(),
          locationUrl: normalizeLocationUrl(values.locationUrl),
        }).unwrap();

        displayAlert({
          open: true,
          message: "Promotion invite updated successfully",
          type: "success",
          timeout: "2500",
        });
        onSuccess?.();
        handleClose();
      } catch (err) {
        displayAlert({
          open: true,
          message:
            err?.data?.message ||
            err?.message ||
            "Unable to update promotion invite",
          type: "error",
          timeout: "3000",
        });
      }
    },
  });

  useEffect(() => {
    if (open && invite) {
      formik.setValues(buildInitialValues(invite));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invite?._id]);

  const handleClose = () => {
    if (isLoading) return;
    formik.resetForm({ values: buildInitialValues(invite) });
    onClose?.();
  };

  const handleReset = () => {
    formik.resetForm({ values: buildInitialValues(invite) });
  };

  const descriptionLength = getDescriptionLength(formik.values.description);
  const eventNameLength = String(formik.values.eventName || "").length;
  const venueLength = String(formik.values.venue || "").length;

  const handleDescriptionChange = (content, _delta, source, editor) => {
    const length = Math.max(0, (editor?.getLength?.() ?? 1) - 1);
    if (length > DESCRIPTION_MAX_LENGTH) return;
    formik.setFieldValue("description", content);
  };

  const bookName = invite?.book?.name || "";
  const venueStillMissing =
    missingSendFields.includes(SEND_FIELD_VENUE) &&
    !String(formik.values.venue || "").trim();
  const timeStillMissing =
    missingSendFields.includes(SEND_FIELD_TIME) &&
    !hasEventTimeValue(formik.values.eventTime);
  const remainingSendFields = [
    venueStillMissing ? SEND_FIELD_VENUE : null,
    timeStillMissing ? SEND_FIELD_TIME : null,
  ].filter(Boolean);
  const venueError = venueStillMissing
    ? "Please fill in Venue Address"
    : formik.touched.venue && formik.errors.venue;
  const timeError = timeStillMissing
    ? "Please fill in Time"
    : formik.touched.eventTime && formik.errors.eventTime;

  const textFieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#fff",
        "& fieldset": { borderColor: "var(--grey300, #d0d5dd)" },
        "&:hover fieldset": { borderColor: "var(--grey400, #98a2b3)" },
        "&.Mui-focused fieldset": { borderColor: "var(--pblue)" },
      },
      "& .MuiInputBase-input": {
        fontSize: "14px",
        padding: "12px 14px",
      },
    }),
    []
  );

  const readOnlyFieldSx = {
    ...textFieldSx,
    "& .MuiOutlinedInput-root": {
      ...textFieldSx["& .MuiOutlinedInput-root"],
      backgroundColor: "var(--grey100, #f2f4f7)",
    },
  };

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : handleClose}
      disableEnforceFocus
      aria-labelledby="edit-promotion-invite-title"
    >
      <Box sx={modalStyle} className="create-promo-invite-modal">
        <form onSubmit={formik.handleSubmit}>
          <div className="create-promo-invite-modal__head">
            <h2 id="edit-promotion-invite-title">Edit Promotion Invite</h2>
            <button
              type="button"
              className="create-promo-invite-modal__close"
              onClick={handleClose}
              disabled={isLoading}
              aria-label="Close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>

          <div className="create-promo-invite-modal__separator" />

          {remainingSendFields.length > 0 && (
            <div className="create-promo-invite-modal__alert" role="alert">
              <WarningAmberRoundedIcon
                className="create-promo-invite-modal__alert-icon"
                fontSize="small"
              />
              <div>
                <p className="create-promo-invite-modal__alert-title">
                  {remainingSendFields.length > 1
                    ? "Please complete the pending fields"
                    : "Please complete the pending field"}
                </p>
                <p className="create-promo-invite-modal__alert-message">
                  {inviteSendBlockedMessage(remainingSendFields)}
                </p>
              </div>
            </div>
          )}

          <div className="create-promo-invite-modal__field">
            <label className="create-promo-invite-modal__label">
              Book Name <RequiredAsterisk />
            </label>
            <TextField
              fullWidth
              value={bookName}
              InputProps={{ readOnly: true }}
              sx={readOnlyFieldSx}
            />
          </div>

          <div className="create-promo-invite-modal__field">
            <label
              className="create-promo-invite-modal__label"
              htmlFor="eventName"
            >
              Event Name <RequiredAsterisk />
            </label>
            <TextField
              id="eventName"
              name="eventName"
              fullWidth
              placeholder="Enter Event Name"
              value={formik.values.eventName}
              onChange={setClampedField(
                formik,
                "eventName",
                PROMOTION_NAME_MAX_LENGTH
              )}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: PROMOTION_NAME_MAX_LENGTH }}
              error={
                formik.touched.eventName && Boolean(formik.errors.eventName)
              }
              sx={textFieldSx}
            />
            <FieldCharCount
              length={eventNameLength}
              max={PROMOTION_NAME_MAX_LENGTH}
            />
            {formik.touched.eventName && formik.errors.eventName && (
              <p className="create-promo-invite-modal__error">
                {formik.errors.eventName}
              </p>
            )}
          </div>

          <div className="create-promo-invite-modal__field">
            <label className="create-promo-invite-modal__label">
              Description <RequiredAsterisk />
            </label>
            <div
              className={`create-promo-invite-modal__quill ${
                formik.touched.description && formik.errors.description
                  ? "create-promo-invite-modal__quill--error"
                  : ""
              }`}
            >
              <ReactQuill
                theme="snow"
                value={formik.values.description}
                onChange={handleDescriptionChange}
                onBlur={() => formik.setFieldTouched("description", true)}
                modules={descriptionQuillModules}
                formats={quillFormats}
                placeholder="Enter description"
              />
            </div>
            <FieldCharCount
              length={descriptionLength}
              max={DESCRIPTION_MAX_LENGTH}
            />
            {formik.touched.description && formik.errors.description && (
              <p className="create-promo-invite-modal__error">
                {formik.errors.description}
              </p>
            )}
          </div>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="create-promo-invite-modal__row">
              <div className="create-promo-invite-modal__field create-promo-invite-modal__field--half">
                <label className="create-promo-invite-modal__label">
                  Select Date <RequiredAsterisk />
                </label>
                <DatePicker
                  value={formik.values.eventDate}
                  onChange={(value) => syncEventDateField(formik, value)}
                  disablePast
                  minDate={promotionTodayPickerValue()}
                  inputFormat="DD/MM/YYYY"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder="Select Date"
                      onBlur={() => formik.setFieldTouched("eventDate", true)}
                      error={
                        formik.touched.eventDate &&
                        Boolean(formik.errors.eventDate)
                      }
                      helperText={
                        formik.touched.eventDate && formik.errors.eventDate
                      }
                      sx={textFieldSx}
                    />
                  )}
                  components={{
                    OpenPickerIcon: CalendarTodayOutlinedIcon,
                  }}
                />
              </div>

              <div className="create-promo-invite-modal__field create-promo-invite-modal__field--half">
                <label className="create-promo-invite-modal__label">
                  Select Time
                </label>
                <PromotionTimePicker
                  value={formik.values.eventTime}
                  onChange={(value) => formik.setFieldValue("eventTime", value)}
                  onBlur={() => formik.setFieldTouched("eventTime", true)}
                  error={Boolean(timeError)}
                  helperText={timeError || ""}
                  textFieldSx={textFieldSx}
                />
              </div>
            </div>
          </LocalizationProvider>

          <div className="create-promo-invite-modal__field">
            <label className="create-promo-invite-modal__label" htmlFor="venue">
              Venue Address
            </label>
            <TextField
              id="venue"
              name="venue"
              fullWidth
              placeholder="Enter Venue Address"
              value={formik.values.venue}
              onChange={setClampedField(formik, "venue", PROMOTION_VENUE_MAX_LENGTH)}
              onBlur={formik.handleBlur}
              inputProps={{ maxLength: PROMOTION_VENUE_MAX_LENGTH }}
              error={Boolean(venueError)}
              sx={textFieldSx}
            />
            <FieldCharCount length={venueLength} max={PROMOTION_VENUE_MAX_LENGTH} />
            {venueError && (
              <p className="create-promo-invite-modal__error">{venueError}</p>
            )}
          </div>

          <div className="create-promo-invite-modal__field">
            <label
              className="create-promo-invite-modal__label"
              htmlFor="locationUrl"
            >
              Location URL
            </label>
            <TextField
              id="locationUrl"
              name="locationUrl"
              fullWidth
              placeholder="Paste location URL"
              value={formik.values.locationUrl}
              onChange={formik.handleChange}
              onBlur={(event) => {
                const next = normalizeLocationUrl(event.target.value);
                if (next !== formik.values.locationUrl) {
                  formik.setFieldValue("locationUrl", next, true);
                }
                formik.handleBlur(event);
              }}
              error={
                formik.touched.locationUrl &&
                Boolean(formik.errors.locationUrl)
              }
              helperText={
                formik.touched.locationUrl && formik.errors.locationUrl
              }
              sx={textFieldSx}
            />
          </div>

          <div className="create-promo-invite-modal__actions">
            <Button
              type="button"
              className="create-promo-invite-modal__btn-cancel"
              variant="contained"
              disableElevation
              onClick={handleReset}
              disabled={isLoading}
            >
              Reset
            </Button>
            <Button
              type="submit"
              className="create-promo-invite-modal__btn-primary"
              variant="contained"
              disableElevation
              disabled={isLoading}
            >
              {isLoading ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </form>
      </Box>
    </Modal>
  );
};
